import { Injectable } from '@angular/core';
import { IUserAPI } from '@core/booking/models/user-api.model';
import { NonPersistentStorage } from '@roller/rdl/utilities/storage';
import { fromEvent, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { CBookingStorageKeys, CStorageKeys } from './storage-keys.const';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  readonly storageKeys = CStorageKeys;
  readonly bookingStorageKeys = CBookingStorageKeys;
  private localStorage: globalThis.Storage | NonPersistentStorage;
  private sessionStorage: globalThis.Storage | NonPersistentStorage;

  constructor() {
    this.localStorage = this.isStorageAvailable('localStorage') ? window.localStorage : new NonPersistentStorage();
    this.sessionStorage = this.isStorageAvailable('sessionStorage')
      ? window.sessionStorage
      : new NonPersistentStorage();
  }

  /**
   * Retrieves a specific item from the browser's local storage.
   * @param key The item to be retrieved.
   * @returns The specified item in local storage if it exists, otherwise null.
   */
  getItem<T>(key: string, isLocal = true): string | null | T {
    let item: string | null | T = this.getStorage(isLocal).getItem(key);
    if (item === 'undefined' || item === 'null') {
      item = null;
    } else {
      item = this.tryParseJSONObject<T>(item as string);
    }

    return item;
  }

  /**
   * Tries to parse the Json string and cast to an expected object.
   * @param jsonString string to try converting to Json/
   * @returns either an expected object (T), or original string.
   */
  tryParseJSONObject<T>(jsonString: string): string | T {
    try {
      const parsedString = JSON.parse(jsonString);
      if (!!parsedString && typeof parsedString === 'object') {
        return parsedString as T;
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}

    return jsonString;
  }

  /**
   * Updates or creates the given item in local storage.
   * @param key The key of the item to create or update.
   * @param value The value of the item to create or update as string.
   */
  setItem(key: string, value: string, isLocal = true): void {
    this.getStorage(isLocal).setItem(key, value);
  }

  /**
   * Removes the given item from local storage.
   * @param key The key of the item to be removed.
   */
  removeItem(key: string, isLocal = true): void {
    this.getStorage(isLocal).removeItem(key);
  }

  /**
   * Clears all the local storage items. Useful for logout events.
   */
  clearStorage(isLocal = true): void {
    this.getStorage(isLocal).clear();
  }

  /** Clear order and booking related items from session storage */
  clearBookingStorage(): void {
    const storageKeys = [...(Object.keys(this.bookingStorageKeys) as (keyof typeof this.bookingStorageKeys)[])];

    storageKeys.forEach((key: keyof typeof this.bookingStorageKeys) =>
      this.removeItem(this.bookingStorageKeys[key], false)
    );
  }

  /** Clears saved ticketUserInfo form details from account details in local storage. */
  clearTicketUserInfo(): void {
    const accountDetails = this.getItem<IUserAPI>(this.storageKeys.accountDetails, true) as IUserAPI;

    if (accountDetails?.ticketUserInfo) {
      delete accountDetails.ticketUserInfo;
      this.setItem(this.storageKeys.accountDetails, JSON.stringify(accountDetails), true);
    }
  }

  /**
   * Observes a given item in local storage and updates observer
   * when the item is updated in another browser tab.
   * @param key The key of the item to be observed.
   * @returns An observable that emits the given item's new value
   * whenever it is updated.
   */
  observeStorageEventItem(key: string, isLocal = true): Observable<string> {
    const storageObservable$ = this.windowStorageEvent().pipe(
      filter((event: StorageEvent) => event.storageArea === this.getStorage(isLocal)),
      filter((event: StorageEvent) => event.key === key),
      map((event: StorageEvent) => event.newValue as string)
    );

    return storageObservable$;
  }

  /**
   * Checks if local or session storage is available.
   * Note Safari (in private mode) has a functional localStorage object (with a quota of zero).
   * Therefore we need to actively set an item to check that it is real and functional.
   * Modified from https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#feature-detecting_localstorage
   */
  isStorageAvailable(type: 'localStorage' | 'sessionStorage') {
    try {
      const storage = window[type];
      const storeText = '__storage_test__';
      storage.setItem(storeText, storeText);
      storage.removeItem(storeText);
      return true;
    } catch (_) {
      return false;
    }
  }

  private windowStorageEvent(): Observable<StorageEvent> {
    return fromEvent<StorageEvent>(window, 'storage');
  }

  /**
   * Fetches the browser's full local storage.
   * @returns A storage object containing methods to access and update the
   * browser's local storage.
   */
  private getStorage(isLocal: boolean): Storage | NonPersistentStorage {
    return isLocal ? this.localStorage : this.sessionStorage;
  }
}
